import { z } from 'zod';
import { ComponentKindSchema, CriticalitySchema } from '../domain/application.js';
import { PathPrefixSchema, TargetHostSchema } from '../domain/target.js';
import { AutoRemediationSchema, SourceRepositorySchema } from './remediation.js';
import { IdSchema, IsoDateTimeSchema, listResponseSchema, OrgIdSchema, PaginationQuerySchema, UserIdSchema, VersionSchema, } from './common.js';
export const APPLICATION_NAME_MAX = 120;
export const APPLICATION_DESCRIPTION_MAX = 2000;
const NameSchema = z.string().trim().min(1).max(APPLICATION_NAME_MAX);
const DescriptionSchema = z.string().trim().max(APPLICATION_DESCRIPTION_MAX);
export const APPLICATION_MAX_COMPONENTS = 40;
export const COMPONENT_MAX_HOSTS = 10;
export const COMPONENT_MAX_PATHS = 20;
/**
 * One part of an application: the web front end, its API, the identity provider, a database.
 *
 * `hosts` and `pathPrefixes` are how the surface map places what a scan observed onto this
 * component — an endpoint belongs to the component whose host matches and whose prefix matches
 * longest. They are a description, never a scope: nothing here makes a host scannable, which is
 * still only an approved scope version (§5). Hosts are held to the same rules as scope hosts so a
 * component cannot name an IP literal that a later scope might copy.
 *
 * `connectsTo` is declared architecture — which component calls which. A scan cannot see it.
 */
const componentShape = {
    id: IdSchema,
    name: z.string().trim().min(1).max(80),
    kind: ComponentKindSchema,
    description: z.string().trim().max(500).optional(),
    hosts: z.array(TargetHostSchema).max(COMPONENT_MAX_HOSTS).default([]),
    pathPrefixes: z.array(PathPrefixSchema).max(COMPONENT_MAX_PATHS).default([]),
    connectsTo: z.array(IdSchema).max(APPLICATION_MAX_COMPONENTS).default([]),
};
export const ComponentSchema = z.object(componentShape);
/**
 * The component list as a whole, checked as a whole: ids and names unique, and every
 * `connectsTo` naming another component in the same list. A dangling reference would draw an
 * edge to nothing, and a self-reference is a loop nobody meant.
 */
export const ComponentsSchema = z
    .array(z.strictObject(componentShape))
    .max(APPLICATION_MAX_COMPONENTS)
    .superRefine((components, ctx) => {
    const ids = new Set();
    const names = new Set();
    components.forEach((c, i) => {
        if (ids.has(c.id))
            ctx.addIssue({ code: 'custom', message: 'Duplicate component id', path: [i, 'id'] });
        ids.add(c.id);
        const name = c.name.toLowerCase();
        if (names.has(name)) {
            ctx.addIssue({
                code: 'custom',
                message: 'Two components share this name',
                path: [i, 'name'],
            });
        }
        names.add(name);
    });
    components.forEach((c, i) => {
        c.connectsTo.forEach((target, j) => {
            if (target === c.id) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'A component cannot connect to itself',
                    path: [i, 'connectsTo', j],
                });
            }
            else if (!ids.has(target)) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'Connects to a component that is not in this application',
                    path: [i, 'connectsTo', j],
                });
            }
        });
    });
});
/**
 * An application under test: the catalogue entry only. Hosts, URLs and test windows are
 * *not* here — they belong to the immutable scope versions of an engagement (§5), which is
 * what keeps "authorised by construction" enforceable below the agent layer.
 *
 * Response schemas are deliberately non-strict so a client on an older build tolerates
 * new fields.
 */
export const ApplicationSchema = z.object({
    id: IdSchema,
    orgId: OrgIdSchema,
    name: NameSchema,
    description: DescriptionSchema.optional(),
    criticality: CriticalitySchema,
    /** Person accountable for the application, not necessarily the engagement owner. */
    ownerUserId: UserIdSchema,
    /** Absent on applications registered before components existed; read as none. */
    components: z.array(ComponentSchema).default([]),
    /** The cloud account this application runs in, for discovery. A pointer, not a permission. */
    cloudConnectionId: IdSchema.optional(),
    /** Where the application's source lives, for proposed fixes. A pointer, not a permission. */
    sourceRepository: SourceRepositorySchema.optional(),
    /** Whether Guardian opens fix pull requests and verifies them on its own, and who said so. */
    autoRemediation: AutoRemediationSchema.optional(),
    /**
     * Set when the application was accepted from a discovery proposal (ADR 0008): which account,
     * which discovery and which proposals. Absent for applications registered by hand.
     */
    origin: z
        .object({
        kind: z.literal('DISCOVERY_PROPOSAL'),
        connectionId: IdSchema,
        discoveryId: IdSchema,
        proposalKeys: z.array(z.string().regex(/^[0-9a-f]{16}$/)).max(10),
    })
        .optional(),
    createdAt: IsoDateTimeSchema,
    createdBy: UserIdSchema,
    updatedAt: IsoDateTimeSchema,
    version: VersionSchema,
});
/**
 * POST /v1/applications
 *
 * `orgId` is taken from the caller's JWT and never accepted from the body — a client must
 * not be able to name another tenant.
 */
export const CreateApplicationRequestSchema = z.strictObject({
    name: NameSchema,
    description: DescriptionSchema.optional(),
    criticality: CriticalitySchema,
    /** Defaults to the caller when omitted. */
    ownerUserId: UserIdSchema.optional(),
    components: ComponentsSchema.default([]),
});
/** GET /v1/applications */
export const ListApplicationsQuerySchema = PaginationQuerySchema.extend({
    criticality: CriticalitySchema.optional(),
});
export const ListApplicationsResponseSchema = listResponseSchema(ApplicationSchema);
export const APPLICATION_PATCH_FIELDS = [
    'name',
    'description',
    'criticality',
    'ownerUserId',
    'components',
    'cloudConnectionId',
    'sourceRepository',
    'autoRemediation',
];
/**
 * Fields only a security administrator may change: each lets Guardian write to a system outside
 * the platform — a repository — on the tenant's behalf.
 */
export const APPLICATION_ADMIN_FIELDS = ['sourceRepository', 'autoRemediation'];
/**
 * PATCH /v1/applications/{id}
 *
 * `expectedVersion` is the `version` the client last read; the repository puts it in a
 * conditional write so a concurrent edit fails with 409 rather than silently winning.
 */
export const PatchApplicationRequestSchema = z
    .strictObject({
    expectedVersion: VersionSchema,
    name: NameSchema.optional(),
    description: DescriptionSchema.optional(),
    criticality: CriticalitySchema.optional(),
    ownerUserId: UserIdSchema.optional(),
    /** Replaces the whole list; `expectedVersion` is what stops two editors losing each other's. */
    components: ComponentsSchema.optional(),
    cloudConnectionId: IdSchema.optional(),
    sourceRepository: SourceRepositorySchema.optional(),
    /**
     * Turns automatic remediation on or off. The server records who and when; a client cannot
     * name someone else as the person it is audited against.
     */
    autoRemediation: z.boolean().optional(),
})
    .refine((body) => APPLICATION_PATCH_FIELDS.some((field) => body[field] !== undefined), {
    message: 'At least one field to update is required',
});
//# sourceMappingURL=application.js.map